import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { ArrowLeft, BookOpen, Lock, Sparkle, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const WPM = 220;

function readingTime(paper) {
  if (!paper) return null;
  // Prefer actual body length when unlocked so free-preview cards + full-post match.
  const words = (paper.body_markdown ? paper.body_markdown.trim().split(/\s+/).length : null) || paper.word_count || 0;
  if (!words) return null;
  const mins = Math.max(4, Math.round(words / WPM));
  return `${mins} min read`;
}

function fmtDate(s) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}

const MD_COMPONENTS = {
  h1: (p) => <h1 className="font-display text-4xl md:text-5xl text-ink mt-12 mb-4 leading-tight" {...p} />,
  h2: (p) => <h2 className="font-display text-3xl md:text-4xl text-ink mt-12 mb-4 leading-tight" {...p} />,
  h3: (p) => <h3 className="font-display text-2xl md:text-3xl text-ink mt-10 mb-3 leading-snug" {...p} />,
  h4: (p) => <h4 className="font-display text-xl text-ink mt-8 mb-2" {...p} />,
  p:  (p) => <p className="text-ink/90 leading-relaxed my-5" {...p} />,
  ul: (p) => <ul className="list-disc pl-6 my-5 space-y-2 text-ink/90 marker:text-forest" {...p} />,
  ol: (p) => <ol className="list-decimal pl-6 my-5 space-y-2 text-ink/90 marker:text-forest" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  blockquote: (p) => <blockquote className="border-l-4 border-ochre pl-5 my-6 italic text-mute" {...p} />,
  strong: (p) => <strong className="font-semibold text-ink" {...p} />,
  em: (p) => <em className="italic text-terra" {...p} />,
  a: (p) => <a className="text-forest underline underline-offset-2 hover:text-forest-dark" {...p} />,
  hr: (p) => <hr className="my-8 border-sand-200" {...p} />,
  code: ({ inline, ...p }) => (
    inline
      ? <code className="px-1.5 py-0.5 rounded bg-sand-100 border border-sand-200 font-mono-data text-sm" {...p} />
      : <code className="block p-4 rounded-xl bg-sand-100 border border-sand-200 font-mono-data text-sm overflow-x-auto" {...p} />
  ),
};

export default function ResearchPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/research/${slug}`);
        setPaper(data);
      } catch {
        toast.error("Paper not found");
        navigate("/research");
      } finally {
        setLoading(false);
      }
    })();
  }, [slug, navigate]);

  useEffect(() => {
    if (!paper) return undefined;
    const SITE = "https://amustforearth.com";
    const canonicalUrl = `${SITE}/research/${paper.slug}`;
    const ogImageVal = `${SITE}/api/research/${paper.slug}/og-card.png`;
    const prevTitle = document.title;
    document.title = `${paper.title} — A Must For Earth Research`;

    const upsertLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      const prev = el.getAttribute("href");
      el.setAttribute("href", href);
      return { el, prev, created };
    };
    const upsertMeta = (attr, name, content) => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      const created = !el;
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, name); document.head.appendChild(el); }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      return { el, prev, created };
    };

    const canonical = upsertLink("canonical", canonicalUrl);
    const desc = upsertMeta("name", "description", paper.abstract || paper.title);
    const ogTitle = upsertMeta("property", "og:title", `${paper.title} — A Must For Earth Research`);
    const ogUrl = upsertMeta("property", "og:url", canonicalUrl);
    const ogDesc = upsertMeta("property", "og:description", paper.abstract || paper.title);
    const ogType = upsertMeta("property", "og:type", "article");
    const ogImage = upsertMeta("property", "og:image", ogImageVal);
    const ogImageW = upsertMeta("property", "og:image:width", "1200");
    const ogImageH = upsertMeta("property", "og:image:height", "630");
    const twCard = upsertMeta("name", "twitter:card", "summary_large_image");
    const twTitle = upsertMeta("name", "twitter:title", paper.title);
    const twDesc = upsertMeta("name", "twitter:description", paper.abstract || paper.title);
    const twImage = upsertMeta("name", "twitter:image", ogImageVal);

    return () => {
      document.title = prevTitle;
      const restore = ({ el, prev, created }) => {
        if (created) { el.parentNode && el.parentNode.removeChild(el); }
        else if (prev != null) {
          if (el.tagName === "LINK") el.setAttribute("href", prev);
          else el.setAttribute("content", prev);
        }
      };
      [canonical, desc, ogTitle, ogUrl, ogDesc, ogType, ogImage, ogImageW, ogImageH, twCard, twTitle, twDesc, twImage].forEach(restore);
    };
  }, [paper]);

  // ------------------------------------------------------------------
  // Reading-progress bar: 0→100% based on scroll through the article.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!paper) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        const doc = document.documentElement;
        const total = doc.scrollHeight - window.innerHeight;
        const y = window.scrollY || doc.scrollTop || 0;
        const pct = total > 0 ? Math.min(100, Math.max(0, (y / total) * 100)) : 0;
        setProgress(pct);
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [paper]);

  if (loading) {
    return (
      <div className="min-h-screen bg-sand-50">
        <Navbar />
        <main className="max-w-3xl mx-auto px-6 py-14 font-mono-data text-mute animate-pulse-soft">loading paper…</main>
      </div>
    );
  }
  if (!paper) return null;

  return (
    <div className="min-h-screen bg-sand-50" data-testid="research-post-page">
      {/* Reading-progress bar — thin forest line that fills as the user scrolls */}
      <div
        aria-hidden
        className="fixed top-0 left-0 right-0 h-[3px] z-[60] pointer-events-none"
        data-testid="reading-progress"
      >
        <div
          className="h-full bg-gradient-to-r from-forest to-ochre transition-[width] duration-100 ease-out shadow-[0_0_8px_rgba(44,76,59,0.35)]"
          style={{ width: `${progress}%` }}
        />
      </div>
      <Navbar />
      <article className="max-w-3xl mx-auto px-6 lg:px-10 py-10">
        <Link to="/research" className="btn-secondary !text-sm" data-testid="back-to-research">
          <ArrowLeft size={14} /> Back to research
        </Link>

        <header className="mt-8">
          <div className="font-mono-data text-mute text-xs flex items-center gap-3 flex-wrap" data-testid="research-post-meta">
            <span>{fmtDate(paper.created_at)}</span>
            {paper.category && (<><span aria-hidden>·</span><span className="capitalize">{paper.category}</span></>)}
            {readingTime(paper) && (
              <>
                <span aria-hidden>·</span>
                <span className="inline-flex items-center gap-1" data-testid="research-post-reading-time">
                  <Clock size={12} weight="duotone" /> {readingTime(paper)}
                </span>
              </>
            )}
            <span className="ml-auto">
              {paper.is_free ? (
                <span className="tag !bg-forest !text-white !border-forest inline-flex items-center gap-1">
                  <Sparkle size={10} weight="fill" /> Free read
                </span>
              ) : (
                <span className="tag !bg-ochre/20 !text-ochre-dark !border-ochre/40 inline-flex items-center gap-1">
                  <Lock size={10} weight="fill" /> Grower
                </span>
              )}
            </span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-ink leading-tight mt-3">{paper.title}</h1>
          {paper.abstract && (
            <div className="mt-6 bg-sand-100 border border-sand-200 rounded-2xl p-5" data-testid="research-abstract">
              <div className="font-mono-data text-mute text-xs mb-2">ABSTRACT</div>
              <p className="text-ink/90 leading-relaxed">{paper.abstract}</p>
            </div>
          )}
          {paper.tags && paper.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-4">
              {paper.tags.map((t, i) => (
                <span key={i} className="tag severity-mild">{t}</span>
              ))}
            </div>
          )}
        </header>

        {paper.image_b64 && (
          <div className="mt-8 rounded-3xl overflow-hidden bg-sand-100 border border-sand-200 aspect-[16/9]">
            <img
              src={`data:image/png;base64,${paper.image_b64}`}
              alt={paper.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}

        {paper.locked ? (
          <>
            <div className="mt-10 space-y-5 text-ink/90 text-lg" data-testid="research-preview">
              <ReactMarkdown components={MD_COMPONENTS}>{paper.preview_markdown || ""}</ReactMarkdown>
            </div>
            <div className="mt-8 bg-gradient-to-br from-ochre/15 to-forest/10 border border-ochre/40 rounded-3xl p-8 text-center" data-testid="paywall-card">
              <Lock size={44} weight="duotone" className="text-ochre-dark mx-auto" />
              <h3 className="font-display text-3xl text-ink mt-3">Unlock the full research paper</h3>
              <p className="text-mute mt-2 max-w-md mx-auto">
                Full text, findings, discussion and every reference — plus every past &amp; future paper —
                comes with your Grower subscription to Climate Plant Analyser.
              </p>
              <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
                <Link to="/pricing" className="btn-primary" data-testid="paywall-upgrade">Upgrade to Grower</Link>
                <Link to="/register" className="btn-secondary">Create a free account</Link>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="mt-10 space-y-5 text-ink/90 text-lg" data-testid="research-body">
              <ReactMarkdown components={MD_COMPONENTS}>{paper.body_markdown || ""}</ReactMarkdown>
            </div>

            {Array.isArray(paper.references) && paper.references.length > 0 && (
              <section className="mt-14" data-testid="research-references">
                <h2 className="font-display text-3xl text-ink mb-4">References</h2>
                <ol className="space-y-3">
                  {paper.references.map((r, i) => (
                    <li key={i} className="flex gap-3 text-sm text-ink/90">
                      <span className="font-mono-data text-mute shrink-0">[{r.number || i + 1}]</span>
                      <span>
                        {r.citation}
                        {r.url && (
                          <>
                            {" "}
                            <a href={r.url} target="_blank" rel="noreferrer" className="text-forest underline underline-offset-2">link</a>
                          </>
                        )}
                      </span>
                    </li>
                  ))}
                </ol>
              </section>
            )}
          </>
        )}

        <div className="mt-16 bg-sand-100 border border-sand-200 rounded-3xl p-8 text-center" data-testid="research-cta">
          <BookOpen size={40} weight="duotone" className="text-forest mx-auto" />
          <h3 className="font-display text-2xl text-ink mt-3">Explore more research</h3>
          <p className="text-mute mt-2">Fresh briefs on Earth, Nature, Wildlife, People and Climate.</p>
          <Link to="/research" className="btn-secondary mt-4 inline-flex">Browse all papers</Link>
        </div>
      </article>
    </div>
  );
}
